import { useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Loaded, Notice, Page, ReasonDialog } from '../components/ui'
import { get, post, put } from '../lib/api'
import { count, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Routing = { redaction_policy: string; store_prompts: boolean; region: string }

type TestResult = {
  redacted: string
  counts: Record<string, number>
  total: number
  policy: string
}

const POLICIES: { id: string; title: string; body: string }[] = [
  {
    id: 'strict',
    title: 'Strict',
    body: 'Every Safe Harbor identifier is replaced, plus free-text names and places. Highest protection, most damage to clinical meaning — a model cannot reason about "the patient" if every noun is a token.',
  },
  {
    id: 'balanced',
    title: 'Balanced',
    body: 'Direct identifiers go; clinical detail stays. The default, and the right answer for co-pilot chat.',
  },
  {
    id: 'clinical',
    title: 'Clinical',
    body: 'Preserves ages, dates and body metrics that change the clinical answer, while removing names, contacts and account identifiers. For report generation where a redacted date would produce a wrong result.',
  },
  {
    id: 'off',
    title: 'Off',
    body: 'No redaction. Only selectable here, never by a patient, and only defensible when the destination is covered by an executed agreement and the request never leaves your own infrastructure.',
  },
]

const SAMPLE = `Patient Aarav Menon, MRN 44192-A, DOB 1979-04-02, called from +971 50 123 4567
about chest tightness after a 6 km run. Reachable at aarav.menon@example.com.
Card on file 4111 1111 1111 1111. Lives at 14 Marina Walk, Dubai.
Resting HR 52, SpO2 97%, BP 118/76. Started atorvastatin 20 mg last month.`

export function PrivacyPolicy() {
  const run = useAction()
  const routing = useResource((signal) => get<Routing>('/v1/admin/ai/routing', undefined, signal))
  const [selected, setSelected] = useState<string | null>(null)
  const [text, setText] = useState(SAMPLE)
  const [result, setResult] = useState<TestResult | null>(null)
  const [testing, setTesting] = useState(false)
  const [testError, setTestError] = useState<string | null>(null)

  const active = routing.data?.redaction_policy ?? 'balanced'

  const runTest = async (policy: string) => {
    setTesting(true)
    setTestError(null)
    try {
      setResult(await post<TestResult>('/v1/admin/privacy/redaction/test', { text, policy }))
    } catch (caught) {
      setTestError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setTesting(false)
    }
  }

  return (
    <Page
      title="PHI policy"
      subtitle="What leaves this system when a request reaches a model provider. The redactor runs a second verification pass after substitution and fails the request closed if any known identifier survived."
    >
      <Loaded resource={routing}>
        {(data) => (
          <>
            {data.redaction_policy === 'off' && (
              <Notice tone="phi">
                <strong>Redaction is currently off.</strong> Raw patient text is reaching model
                providers. This is only defensible under an executed agreement with every provider
                in the routing chain.
              </Notice>
            )}

            <div className="grid grid--2">
              {POLICIES.map((policy) => {
                const isActive = policy.id === active
                return (
                  <Card
                    key={policy.id}
                    title={
                      <span className="row" style={{ gap: 8 }}>
                        {policy.title}
                        {isActive && <Badge tone="green">Active</Badge>}
                        {policy.id === 'off' && <Badge tone="alert">Dangerous</Badge>}
                      </span>
                    }
                    action={
                      isActive ? null : (
                        <button
                          className={policy.id === 'off' ? 'btn btn--sm btn--danger' : 'btn btn--sm'}
                          onClick={() => setSelected(policy.id)}
                        >
                          Make active
                        </button>
                      )
                    }
                  >
                    <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                      {policy.body}
                    </p>
                  </Card>
                )
              })}
            </div>
          </>
        )}
      </Loaded>

      <Card
        title="Live tester"
        hint="Paste real-shaped text and see exactly what the gateway would send"
      >
        <div className="stack">
          <Notice>
            The sample below is synthetic. Do not paste a real patient's record into this box to
            find out whether it would be redacted — that request is itself a disclosure, and it is
            logged.
          </Notice>

          <div className="field">
            <label className="field__label" htmlFor="tester">
              Input
            </label>
            <textarea
              id="tester"
              className="textarea"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </div>

          <div className="row row--wrap">
            {POLICIES.map((policy) => (
              <button
                key={policy.id}
                className="btn btn--sm"
                disabled={testing || !text.trim()}
                onClick={() => void runTest(policy.id)}
              >
                Test “{policy.title}”
              </button>
            ))}
          </div>

          {testError && <Notice tone="warn">{testError}</Notice>}

          {result && (
            <>
              <div className="field">
                <label className="field__label">
                  What would be sent · policy <span className="mono">{result.policy}</span> ·{' '}
                  {count(result.total)} substitution{result.total === 1 ? '' : 's'}
                </label>
                <pre
                  className="mono"
                  style={{
                    margin: 0,
                    padding: 14,
                    background: 'var(--surface-inset)',
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    whiteSpace: 'pre-wrap',
                    fontSize: 12,
                    lineHeight: 1.55,
                  }}
                >
                  {result.redacted}
                </pre>
              </div>
              <div className="row row--wrap" style={{ gap: 8 }}>
                {Object.entries(result.counts).length === 0 ? (
                  <span className="dim">Nothing matched — the text carried no detectable identifiers.</span>
                ) : (
                  Object.entries(result.counts).map(([category, n]) => (
                    <Badge key={category} tone="cyan">
                      {titleCase(category)} × {n}
                    </Badge>
                  ))
                )}
              </div>
              <p className="dim" style={{ fontSize: 11.5, margin: 0 }}>
                Placeholders are stable within a request — the same name always becomes the same
                token — so a model can still reason about "who did what" without ever seeing who.
                The mapping is held in memory for the life of the request and used to rehydrate the
                answer before it reaches the app.
              </p>
            </>
          )}
        </div>
      </Card>

      {selected && (
        <ReasonDialog
          title={`Switch redaction policy to “${selected}”`}
          description={
            selected === 'off'
              ? 'This sends raw patient text to model providers. Name the agreement and the deployment that makes this lawful.'
              : 'Changing the policy changes what leaves this system for every user under it.'
          }
          confirmLabel="Change policy"
          danger={selected === 'off'}
          minLength={10}
          onClose={() => setSelected(null)}
          onConfirm={(reason) =>
            run(
              () => put('/v1/admin/privacy/policy', { redaction_policy: selected, reason }),
              `Redaction policy set to ${selected}.`,
              routing.reload,
            )
          }
        />
      )}
    </Page>
  )
}
